import { LocalDate } from '@/time';
import type { ReportConfig, ReportData, ReportSettings, ReportType, UploadedBackground } from '@/SocialReports/types';
import { addDays, format } from 'date-fns';
import { formatXSSDetails, baseRequest, getRequest, type XSSDetails } from '@/Util/utils';
import type { Exercise } from '@/Common/ActivityFunctions';
import { saveAs } from 'file-saver';
import { type SelectedMode, type SpecificityTypes, type MinutePower, type ProgramFocusTypes, type Signature, type ActivityRecordsData, type GroupSharingOptions, type ReportKeys, type SharedActivity, type XertSegment, type StravaSegmentInfo, workoutPlayersThatWeSendWorkoutTo} from "../Common/constants";
import route from "./routes";
import type { UserStatsResponse, RankingActivityOption } from "@/MyFitness/Rankings/rankingsTypes";
import { errorMessageFrom, errorCodeFrom, type ErrorFallback } from "./notify";
import { ErrorCode } from "./errorCodes";
import type { SeasonsResponse } from "@/MySeasons/seasons";

//@ts-ignore
const Xert = window.Xert;

function simpleResponse<T>(response: Response): Promise<T> {
    if (response.ok) return response.json()
    throw `${response.status} ${response.statusText}`
}

/**
 * Best-effort user-facing reason for a failed `fetch`. Reads `message`/`error` and `error_code` off
 * a JSON body when the endpoint sent them, and otherwise falls back to `fallback` rather than
 * showing the user a raw status line or an HTML error page.
 */
export async function errorFromResponse(response: Response, fallback: ErrorFallback): Promise<BasicResponse> {
  let body: unknown = null;
  try {
    body = await response.clone().json();
  } catch {
    // Not JSON (an HTML error page, an empty body) — `fallback` it is.
  }
  return {
    success: false,
    message: errorMessageFrom(body) ?? fallback.message,
    error_code: errorCodeFrom(body) ?? fallback.code,
  };
}

/** What to show when the request never reached the server at all. */
export const NETWORK_ERROR: ErrorFallback = {
  code: ErrorCode.NETWORK_UNREACHABLE,
  message: 'Could not reach Xert. Please check your connection and try again.',
};

/**
 * Resolves a request into a `{ success, message }` object without ever throwing, so callers can
 * always tell the user *why* something failed.
 *
 * Only an explicit `success: true` from the endpoint counts as success: several of these endpoints
 * echo a third party's raw error body back on failure, and that body would otherwise parse into a
 * perfectly valid — but entirely unsuccessful — JSON object.
 *
 * `fallback` is optional. Leave it off when the caller can word the failure better than this layer
 * can — `message` and `error_code` are then left unset unless the endpoint supplied them, so the
 * caller's own fallback is what reaches the user.
 */
async function asBasicResponse<T extends BasicResponse>(request: Promise<Response>, fallback?: ErrorFallback): Promise<T> {
  let response: Response;
  try {
    response = await request;
  } catch (e) {
    console.error('Request failed', e);
    return { success: false, ...NETWORK_ERROR_RESPONSE } as T;
  }

  const data = await response.json().catch(() => null);
  if (data && typeof data === 'object' && data.success === true) return data as T;

  const message = errorMessageFrom(data) ?? fallback?.message;
  const error_code = errorCodeFrom(data) ?? fallback?.code;
  return {
    ...(data ?? {}),
    success: false,
    ...(message ? { message } : {}),
    ...(error_code ? { error_code } : {}),
  } as T;
}

/* ===================================
    Calendar APIs
====================================== */

export type BasicResponse = {
  success?: boolean;
  message?: string;
  /** Some older endpoints answer with `error` instead of `message`. */
  error?: string;
  /** The number shown to the user alongside `message`. See `errorCodes.ts`. */
  error_code?: number;
  /** `'warning'` shows `message` on its own, with no number. See `notifyApiError`. */
  severity?: 'error' | 'warning';
};

/** `NETWORK_ERROR` in the shape a failed `BasicResponse` wants. */
const NETWORK_ERROR_RESPONSE = { message: NETWORK_ERROR.message, error_code: NETWORK_ERROR.code };

export type SPFocus = {
  tSPR: string;
  focus: {
    name: string;
    workoutname?: string; // TODO: Should we just remove these unused fields?
    duration?: number;
    per_weight?: boolean;
    db_name?: string;
    desc?: string;
  };
  sig?: Signature;
  minutePower?: number;
};

type ExerciseData = Object;
export type ForecastResult = {
  forecast_activities: ExerciseData;
  activities: ExerciseData[][];
  training_status_collection: ExerciseData[][];
  target_event: any;
  target_event_ts: number | null;
  start_forecast: any;
  spFocus: SPFocus[];
  last_BT_date: Date | null;
};

export type WorkoutPlayerType = 'garmin_connect' | 'zwift' | 'xert_ebc' | 'wahoo' | 'mywhoosh' | 'rouvy' | 'garmin_workout_player' | 'rungap' | 'ictrainer';
// How a mixed mode workout's slope intervals are sent to a player or file that only supports ERG mode.
export type SlopeIntervals = 'free_ride' | 'power';

export interface WorkoutPlayer {
  type?: WorkoutPlayerType;
  workout?: Exercise & { training_source?: string, sent_at?: number, sent_from?: string}; // may be undefined if none sent yet
  status?: 'sending' | 'reseting' | 'sent' | 'received' | 'failed' | 'connecting';
  updatedAt?: Date;
}

export async function populateForecast(selected_mode: string, fromDate: Date = null): Promise<ForecastResult | null> {
  if (fromDate == null) fromDate = new Date();
  fromDate.setHours(12);
  fromDate.setMinutes(0);
  fromDate.setSeconds(0);
  let fromDateStr = format(fromDate, 'yyyy-MM-dd'); // This is the local date so that forActivity is the middle card


  const response = await window.fetch(`/calendar/forecast-activities-close/${fromDateStr}`, {
    method: 'GET',
    ...getRequest(),
  });
  const data = await response.json();
  if (data.activities && data.training_status_array) {
    return {
      activities: data.activities,
      forecast_activities: data.activities.map((this_activities) => this_activities.find((a) => a.state?.includes('forecast') && !a.state?.includes('completed'))),
      training_status_collection: data.training_status_array,
      target_event: data.target_event ?? null,
      target_event_ts: data.target_event_ts ?? null,
      start_forecast: data.start_forecast ?? null,
      spFocus: data.focusSPR,
      last_BT_date: data.last_BT_date ? new Date(data.last_BT_date) : null,
    };
  } else {
    if (selected_mode == 'target_xss') Xert.warning('Unable to fetch forecast information. Please try again later');
    return null;
  }
}

export type CalendarEventBody = {
  id: string;
  type: string;
  start_date: string;
  end_date: string;
  forUser: string;
  from_xata: number;
  options: {
    edit_id?: string;
    state?: [string] | null;
  } & any;
};

export async function deleteCalendarEvent(oldEventToDeletePath: string): Promise<any> {
  const deletionBody = {
    id: oldEventToDeletePath,
  };
  return window.fetch('/deleteCalendarEvent', {
    ...getRequest(),
    method: 'POST',
    body: JSON.stringify(deletionBody),
  });
}

export async function createCalendarEvent(body: CalendarEventBody) {
  return window.fetch('/createCalendarEvent', {
    ...getRequest(),
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function lockCalendarEvent(body) {
  return window.fetch('/pinCalendarEvent', {
    ...getRequest(),
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/* ===================================
    Program Page APIs
====================================== */

export async function getMinutePowers(): Promise<MinutePower[]> {
  return fetch('/minute-powers').then((r) => r.json());
}

export async function initForecastInputs(targetEvent: { start: Date; startDate?: Date | null; xss?: number; focus_power?: number; focus: ProgramFocusTypes; specificity_rating: SpecificityTypes; mode: SelectedMode }) {
  const { startDate, ...targetEventParams } = targetEvent
  return window
    .fetch(
      route('CalendarController@getTrainingForecast') +
        '?' +
        new URLSearchParams({
          duration: '-1', //value of -1 means "up to next event"
          title: 'Test Event',
          ...targetEventParams,
          // Plan starts on a future date: the forecast runs from that date (see TrainingForecastJob)
          ...(startDate ? { fromDate: LocalDate.format(startDate) } : {}), // date only, the server reads it in the profile timezone
          start: targetEvent.start.toISOString(),
          xss: targetEvent.xss?.toString(),
          focus_power: targetEvent.focus_power?.toString(),
        }),
      {
        ...getRequest(),
      },
    )
    .then((r) => r.json());
}

export async function getMySeasonsData(startMonth: number, endMonth: number, years?: number[]): Promise<SeasonsResponse> {
  const params = new URLSearchParams({ start_month: String(startMonth), end_month: String(endMonth) });
  years?.forEach((year) => params.append('years[]', String(year)));
  const response = await window.fetch(`${route('MySeasonsController@getMySeasonsData')}?${params}`, getRequest());
  return simpleResponse<SeasonsResponse>(response);
}

/** A stored plan report ({report}), or the status of the plan in progress ({available, report}) for planId 'current'. null: no such report. */
export async function getPlanReport(planId: string, forUser?: string | null, fresh = false): Promise<{ available?: boolean; report?: any } | null> {
  const query: Record<string, string> = {};
  if (forUser) query.forUser = forUser;
  if (fresh) query.fresh = '1';   // after a recalculation: not the copy kept for ten minutes
  const params = Object.keys(query).length ? `?${new URLSearchParams(query)}` : '';
  const url = planId === 'current' ? `/plan-status/data${params}` : `/plan-report/${encodeURIComponent(planId)}/data${params}`;
  const response = await window.fetch(url, getRequest());
  if (response.status === 404) return null;
  return simpleResponse(response);
}

export const getTLFitSigFromHistory = async (start: Date, end: Date, fd: number) => {
  return window
    .fetch(
      'program/sigtl-history' +
        '?' +
        new URLSearchParams({
          start_date: start.toISOString(),
          end_date: end.toISOString(),
          fd: fd.toString(),
        }),
      getRequest(),
    )
    .then((r) => r.json());
};

export async function autoGenWorkout({ forDate, edit_id, bucketsInfo, targets }: { forDate: Date; edit_id?: string; bucketsInfo?: { intervals: any[]; challengeLevel: number }; targets?: XSSDetails<number> }) {
  let body = {
    forDate,
    options: {
      state: ['recommended', 'scheduled'],
    },
  };
  if (edit_id) {
    body.options['edit_id'] = edit_id;
    body.options.state.push('forecast');
    body.options['planned'] = true;
  }
  if (bucketsInfo) {
    body.options['intervals'] = bucketsInfo.intervals;
    body.options['challenge_level'] = bucketsInfo.challengeLevel;
  }
  if (targets) {
    body.options['targets'] = targets;
  }
  console.log(body);
  return window
    .fetch(route('WorkoutController@autogenWorkout'), {
      ...getRequest(),
      method: 'POST',
      body: JSON.stringify(body),
    })
    .then((r) => r.json());
}

export async function getConnectedWorkoutPlayers(): Promise<WorkoutPlayer[]> {
  return window
    .fetch(route('WorkoutController@getConnectedWorkoutPlayers'), {
      ...getRequest(),
    })
    .then((r) => r.json())
    .then((data) => {
      const result: WorkoutPlayer[] = [];
      for (const player of data.workout_players) {
        let workout = player['workout']
        result.push({
          type: player['type'],
          workout: workout,
          status: workout?.['sent_from'] ? 'received' : 'sent',
          updatedAt: new Date(),
        });
      }
      return result;
    });
}

/**
 * No fallback message: every caller reaches this through `connectedWorkoutPlayers.sendWorkoutToWorkoutPlayer`,
 * which names the app the workout was going to.
 */
export async function sendWorkoutToWorkoutPlayer(workoutPlayer: WorkoutPlayerType, workoutPath: string | null, slopeIntervals?: SlopeIntervals): Promise<BasicResponse & { workout?: Exercise }> {
  if (workoutPlayer == 'garmin_connect' || workoutPlayer == 'zwift' || workoutPlayer == 'wahoo') {
    let routeName = workoutPlayer == 'garmin_connect' ? 'garmin' : workoutPlayer;
    return asBasicResponse(
      window.fetch(`/workout/to-${routeName}-workout/${workoutPath}`, {
        ...getRequest(),
        method: 'POST',
        body: JSON.stringify({ slope_intervals: slopeIntervals }),
      }),
    );
  } else {
    let body = {
      workout_path: workoutPath,
      workout_player: workoutPlayer,
      slope_intervals: slopeIntervals,
    };
    return asBasicResponse(
      window.fetch(`/workout/set-workout-player-override`, {
        ...getRequest(),
        method: 'POST',
        body: JSON.stringify(body),
      }),
    );
  }
}

export async function addConnectedWorkoutPlayer(workoutPlayer: WorkoutPlayerType): Promise<BasicResponse & { workout?: Exercise }> {
  if (workoutPlayersThatWeSendWorkoutTo.includes(workoutPlayer)) {
    return { success: false, message: 'Cannot manually add this workout player' };
  }
  let body = {
    workout_player: workoutPlayer,
  };

  return window
    .fetch(route('WorkoutController@addConnectedWorkoutPlayer'), {
      ...getRequest(),
      method: 'POST',
      body: JSON.stringify(body),
    })
    .then((r) => r.json());
}

export async function removeConnectedWorkoutPlayer(workoutPlayer: WorkoutPlayerType): Promise<BasicResponse> {
  if (workoutPlayersThatWeSendWorkoutTo.includes(workoutPlayer)) {
    return { success: false, message: 'Cannot manually remove this workout player' };
  }
  return window
    .fetch(route('WorkoutController@removeConnectedWorkoutPlayer') +
      '?' +
        new URLSearchParams({
          workout_player: workoutPlayer
        }), {
      ...getRequest(),
      method: 'delete',
    })
    .then((r) => r.json())
}

export type XSSTargetsResponse = {
  originalTargets: XSSDetails<string>;
  originalTargetsNumeric?: XSSDetails<number>;
  completed: XSSDetails<string>;
  remaining: XSSDetails<string>;
  remainingTargetsNumeric?: XSSDetails<number>;
  currentTargets: XSSDetails<string>; // usually either remaining XSS or user overridden
  source: string; // 'XFAI' | 'XATA' | 'Override' | 'FF_current' | 'FF_historical' | exerciseType
  recommendedRecoveryDays: { low: number, high: number, peak: number};
};

export function convertDataToXSSTargetsResponse(data: any): XSSTargetsResponse {
  const result: XSSTargetsResponse = {
    currentTargets: formatXSSDetails({
      xss: data['targetXSS']['xss'] ?? 0,
      xlss: data['targetXSS']['xlss'] ?? 0,
      xhss: data['targetXSS']['xhss'] ?? 0,
      xpss: data['targetXSS']['xpss'] ?? 0,
    }),
    originalTargets: formatXSSDetails({
      xss: data['originalTargetXSS']['xss'] ?? 0,
      xlss: data['originalTargetXSS']['xlss'] ?? 0,
      xhss: data['originalTargetXSS']['xhss'] ?? 0,
      xpss: data['originalTargetXSS']['xpss'] ?? 0,
    }),
    originalTargetsNumeric: {
      xss: data['originalTargetXSS']['xss'] ?? 0,
      xlss: data['originalTargetXSS']['xlss'] ?? 0,
      xhss: data['originalTargetXSS']['xhss'] ?? 0,
      xpss: data['originalTargetXSS']['xpss'] ?? 0,
    },
    completed: formatXSSDetails({
      xss: data['completedXSS']['xss'] ?? 0,
      xlss: data['completedXSS']['xlss'] ?? 0,
      xhss: data['completedXSS']['xhss'] ?? 0,
      xpss: data['completedXSS']['xpss'] ?? 0,
    }),
    remaining: formatXSSDetails({
      xss: data['remainingXSS']['xss'] ?? 0,
      xlss: data['remainingXSS']['xlss'] ?? 0,
      xhss: data['remainingXSS']['xhss'] ?? 0,
      xpss: data['remainingXSS']['xpss'] ?? 0,
    }),
    remainingTargetsNumeric: {
      xss: data['remainingXSS']['xss'] ?? 0,
      xlss: data['remainingXSS']['xlss'] ?? 0,
      xhss: data['remainingXSS']['xhss'] ?? 0,
      xpss: data['remainingXSS']['xpss'] ?? 0,
    },
    source: data['source'],
    recommendedRecoveryDays: data['recommendedRecoveryDays'],
  }
  return result;
}

export async function getXSSTargets(date?: Date): Promise<XSSTargetsResponse> {
  const url = date
    ? `my-fitness/xss_targets?${new URLSearchParams({ date: date.toISOString() })}`
    : `my-fitness/xss_targets`;
  return window
    .fetch(url, {
      ...getRequest(),
    })
    .then((r) => r.json())
    .then(convertDataToXSSTargetsResponse);
}

export async function overrideXSSTargets(xssTargets: XSSDetails<number>): Promise<XSSTargetsResponse> {
  return window
    .fetch(`my-fitness/xss_targets`, {
      ...getRequest(),
      method: 'POST',
      body: JSON.stringify(xssTargets),
    })
    .then((r) => r.json())
    .then(convertDataToXSSTargetsResponse);
}

export async function resetUserOverriddenXSSTargets(): Promise<XSSTargetsResponse> {
  return window
    .fetch(`my-fitness/reset_xss_targets`, {
      ...getRequest(),
      method: 'POST',
    })
    .then((r) => r.json())
    .then(convertDataToXSSTargetsResponse);
}

export async function switchKeptDuplicate(keep_path: string): Promise<any> {
  return window
    .fetch(`/activities/switch-duplicate`, {
      ...getRequest(),
      method: 'POST',
      body: JSON.stringify({ keep_path }),
    })
    .then((r) => r.json());
}

export async function unlinkDuplicate(activity_path: string): Promise<any> {
  return window
    .fetch(`/activities/unlink-duplicate`, {
      ...getRequest(),
      method: 'POST',
      body: JSON.stringify({ activity_path }),
    })
    .then((r) => r.json());
}

export type GetTrainingResponse = {
  exercises: Exercise[];
  training_advice;
}

export async function getRecommendedTraining(date: Date): Promise<GetTrainingResponse> {
  return window
    .fetch(`recommended-training` +
        '?' +
        new URLSearchParams({
          recent: true.toString(),
          date: date?.toISOString() ?? '',
          additional: false.toString(),
        }),
      getRequest(),
    )
    .then((r) => r.json());
}

// returns workouts sorted in recommended order
export async function getAllWorkouts(date: Date, url: String = '/workouts/all-workouts'): Promise<GetTrainingResponse> {
  return window
    .fetch(url +
        '?' +
        new URLSearchParams({
          date: date?.toISOString() ?? '',
        }),
      getRequest(),
    )
    .then((r) => r.json());
}

export async function getRecommendedTrainingLists(date: Date, lists: {name: string, favourite?: boolean, recent?: boolean, dayOfWeek?: number}[]): Promise<Record<string, Exercise[]>> {
  return window
    .fetch(`recommended-training-lists` +
        '?' +
        new URLSearchParams({
          lists: JSON.stringify(lists),
          date: date.toISOString(),
        }),
      getRequest(),
    )
    .then((r) => r.json())
    // .then((data) => {
    //   const result: Record<string, Exercise[]> = {};
    //   for (const key in data) {
    //     result[key] = data[key].map((exercise: any) => ({
    //       name: exercise.name,
    //       description: exercise.description,
    //       path: exercise.path,
    //     }));
    //   }
    //   return result;
    // });
}

/** True when the workout has slope intervals as well as ERG intervals. */
export async function getWorkoutMixedMode(path: string): Promise<boolean> {
  const response = await window.fetch(route('WorkoutController@getWorkoutMixedMode', [path]), getRequest());
  if (!response.ok) throw new Error(`Could not check workout ${path} for slope intervals`);
  return !!(await response.json()).mixed_mode;
}

export async function downloadWorkoutFile(path: string, name: string, format: 'zwo' | 'tcx' | 'erg', slopeIntervals?: SlopeIntervals): Promise<BasicResponse> {
  const fallback: ErrorFallback = {
    code: ErrorCode.WORKOUT_EXPORT_FAILED,
    message: `Could not build the ${format.toUpperCase()} file for this workout. Please try again in a moment.`,
  };
  try {
    const response = await window.fetch(
      route('WorkoutController@getConvertWorkout', [path]) +
        '?' +
        new URLSearchParams({
          [format]: true.toString(),
          ...(slopeIntervals ? { slope_intervals: slopeIntervals } : {}),
        }),
      getRequest(),
    );

    if (!response.ok) return await errorFromResponse(response, fallback);

    const data = await response.blob();
    // The converter answers 200 with an empty body when it has nothing to export, so an empty blob
    // is a failure rather than a zero-length workout.
    if (!data.size) return { success: false, message: fallback.message, error_code: fallback.code };

    let fileName = name?.trim().length ? `${name.trim()} - Xert` : 'Untitled Workout - Xert';
    let blob = new Blob([data], { type: "text/plain;charset=utf-8" });
    saveAs(blob, `${fileName}.${format}`);
    return { success: true };
  } catch (e) {
    console.error('Failed to download workout file', e);
    return { success: false, ...NETWORK_ERROR_RESPONSE };
  }
}

export async function downloadActivityFitFile(activityPath: string, name: string): Promise<BasicResponse> {
  const fallback: ErrorFallback = {
    code: ErrorCode.ACTIVITY_FILE_UNAVAILABLE,
    message: "Could not download this activity's original file. It may no longer be stored on our servers.",
  };
  try {
    const response = await window.fetch(
      route('ActivityController@downloadActivityFile', [activityPath]),
      getRequest(),
    );

    if (!response.ok) return await errorFromResponse(response, fallback);

    const data = await response.blob();
    if (!data.size) return { success: false, message: fallback.message, error_code: fallback.code };

    let fileName = name?.trim().length ? `${name.trim()} - Xert` : 'Untitled Activity - Xert';
    let blob = new Blob([data], { type: "text/plain;charset=utf-8" });
    saveAs(blob, `${fileName}.fit`);
    return { success: true };
  } catch (e) {
    console.error('Failed to download activity file', e);
    return { success: false, ...NETWORK_ERROR_RESPONSE };
  }
}

export async function scheduleTraining(exercise: Exercise, dateTime: Date, assignToForecast: boolean = false): Promise<BasicResponse> {
  let now = new Date();
  // No training to schedule means we lost track of it, which the user can do nothing about: an
  // error, with a number. The other two are just the form being wrong — warnings, no number.
  if (!exercise) return { success: false, message: 'There is no training selected to schedule.', error_code: ErrorCode.SCHEDULE_NO_TRAINING };
  if (!dateTime || isNaN(dateTime.getTime())) return { success: false, message: 'Please choose a valid date and time.', severity: 'warning' };
  if (dateTime < now) return { success: false, message: 'Training can only be scheduled for a date and time in the future.', severity: 'warning' };

  let postData = {};
  postData['start_date'] = dateTime.toISOString();
  postData['manualExercise'] = false;
  postData['type'] = exercise.exerciseType?.toLowerCase()
  postData['id'] = exercise.path;
  postData['end_date'] = new Date(dateTime.getTime() + exercise.duration*1000).toISOString();

  postData['options'] = {
    state: ['scheduled', 'recommended']
  };
  if (assignToForecast) {
    postData['options']['assign_to_forecast'] = true;
  }

  const fallback: ErrorFallback = {
    code: assignToForecast ? ErrorCode.SCHEDULE_FORECAST_FAILED : ErrorCode.SCHEDULE_CALENDAR_FAILED,
    message: assignToForecast
      ? 'Could not assign this training to your forecast. Please try again in a moment.'
      : 'Could not add this training to your calendar. Please try again in a moment.',
  };
  try {
    const response = await window.fetch('/createCalendarEvent', {method: 'POST', body: JSON.stringify(postData), ...getRequest()});
    if (!response.ok) return await errorFromResponse(response, fallback);

    const data = await response.json().catch(() => null);
    if (data && data.success === false) {
      return {
        success: false,
        message: errorMessageFrom(data) ?? fallback.message,
        error_code: errorCodeFrom(data) ?? fallback.code,
      };
    }

    return { success: true };
  } catch (e) {
    console.error('Failed to schedule training', e);
    return { success: false, ...NETWORK_ERROR_RESPONSE };
  }
}

export type StravaActivityUploadStatusResponse = {
  status_code: 'not_sent' | 'processing' | 'ready' | 'error' | 'deleted',
  message: string,
  status?: string, // status returned by Strava API
  error?: string, // error returned by Strava API
  activity_url?: string, // URL to the Strava activity if available
  activity_id?: string, // Strava activity ID if available
}

export async function getStravaActivityUploadStatus(xertActivityPath: string): Promise<StravaActivityUploadStatusResponse> {
  return window
    .fetch(`/strava/get-upload-status/${xertActivityPath}`, {
      ...getRequest(),
    })
    .then((r) => r.json());
}

export type SendActivityToStravaResponse = BasicResponse & {
  id: string, // Strava upload ID (not the activity id)
}

export async function sendActivityToStrava(path: string, body?: any): Promise<SendActivityToStravaResponse> {
  return asBasicResponse(
    window.fetch(`/strava/upload-activity/${path}`, {
      method: 'POST',
      ...getRequest(),
      body: JSON.stringify(body),
    }),
    {
      code: ErrorCode.STRAVA_SEND_FAILED,
      message: 'Could not send this activity to Strava. Please try again, or reconnect Strava in your Sync settings.',
    },
  );
}

/** Re-render the Xert report on an activity already on Strava; `report` is the design from the send dialog. */
export async function uploadActivityStravaReport(path: string, report?: ReportConfig): Promise<SendActivityToStravaResponse> {
  return asBasicResponse(
    window.fetch(`/strava/upload-report/${path}`, {
      method: 'POST',
      ...getRequest(),
      body: JSON.stringify({ report }),
    }),
    {
      code: ErrorCode.STRAVA_REPORT_FAILED,
      message: 'Could not update the Xert report on your Strava activity. Please try again in a moment.',
    },
  );
}

export type StravaActivityUploadFieldsResponse = {
  success: boolean,
  message?: string,
  body_params?: {
    name: string,
    description: string,
    sport_type: string,
    report_type: ReportType | null, // the report sent automatically for this activity, if any
  },
}

export async function getStravaActivityUploadFieldDefaults(xertActivityPath: string): Promise<StravaActivityUploadFieldsResponse> {
  return window
    .fetch(`/strava/upload-activity-field-defaults/${xertActivityPath}`, {
      ...getRequest(),
    })
    .then((r) => r.json());
}

export type SocialReportDataResponse = BasicResponse & {
  data: ReportData,
  settings: ReportSettings,
}

/** Everything the send dialog needs to preview and customise the report for an activity. */
export async function getSocialReportData(path: string): Promise<SocialReportDataResponse> {
  return asBasicResponse(
    window.fetch(`/social-reports/${path}/data`, { ...getRequest() }),
    { code: ErrorCode.SOCIAL_REPORT_DATA_FAILED, message: 'Could not load the report designs. Please try again.' },
  );
}

export type SocialReportActivity = { path: string, name: string | null, start_date: string }

export type SocialReportPreviewResponse = BasicResponse & {
  data: ReportData | null, // the chosen (default: latest) activity, if the user has any
  sample_stock_backgrounds: string[], // cycling stock backgrounds for the sample ride, when data is null
  activities: SocialReportActivity[], // recent activities to pick from, newest first
  settings: ReportSettings,
  show_on_group_reports: boolean, // whether the user's name and photo show on other riders' group reports
}

/**
 * For the Activity Reports page: an activity to preview designs with (`path`, or the latest),
 * the user's recent activities, and their saved designs.
 */
export async function getSocialReportPreview(path?: string): Promise<SocialReportPreviewResponse> {
  const query = path ? `?activity=${encodeURIComponent(path)}` : '';
  return asBasicResponse(
    window.fetch(`/social-reports/preview${query}`, { ...getRequest() }),
    { code: ErrorCode.SOCIAL_REPORT_DATA_FAILED, message: 'Could not load your report designs. Please try again.' },
  );
}

/** Make a design the default for its report type. */
export async function saveSocialReportDefault(config: ReportConfig): Promise<BasicResponse & { settings: ReportSettings }> {
  return asBasicResponse(
    window.fetch('/social-reports/settings', { ...getRequest(), method: 'POST', body: JSON.stringify(config) }),
    { code: ErrorCode.SOCIAL_REPORT_SAVE_FAILED, message: 'Could not save your report design. Please try again.' },
  );
}

/** Render a design for an activity as a JPEG, to download or share. */
export async function renderSocialReportImage(path: string, config: ReportConfig): Promise<BasicResponse & { file?: File }> {
  const fallback = { code: ErrorCode.SOCIAL_REPORT_IMAGE_FAILED, message: 'Could not create the report image. Please try again in a moment.' };
  let response: Response;
  try {
    response = await window.fetch(`/social-reports/${path}/image`, { ...getRequest(), method: 'POST', body: JSON.stringify(config) });
  } catch (e) {
    console.error('Request failed', e);
    return { success: false, ...NETWORK_ERROR_RESPONSE };
  }
  if (!response.ok || !response.headers.get('Content-Type')?.startsWith('image/')) {
    return asBasicResponse(Promise.resolve(response), fallback);
  }
  const name = /filename="?([^";]+)"?/.exec(response.headers.get('Content-Disposition') ?? '')?.[1] ?? 'xert-report.jpg';
  return { success: true, file: new File([await response.blob()], name, { type: 'image/jpeg' }) };
}

/**
 * Upload a report background. A temporary one (from an activity's send or share dialog) is only
 * for the report being made, and isn't added to the user's saved backgrounds.
 */
export async function uploadSocialReportBackground(file: File, temporary = false): Promise<BasicResponse & { background: UploadedBackground }> {
  const body = new FormData();
  body.append('image', file);
  if (temporary) body.append('temporary', '1');
  return asBasicResponse(
    window.fetch('/social-reports/backgrounds', { ...baseRequest(), method: 'POST', body }),
    { code: ErrorCode.SOCIAL_REPORT_BACKGROUND_FAILED, message: 'Could not upload that image. Please try a JPEG or PNG under 15 MB.' },
  );
}

export type StockBackgroundSport = 'cycling' | 'running' | 'all';
export type StockBackground = { id: string, url: string, sport: StockBackgroundSport, uploaded: boolean, created_at: string | null };

// Admin: the stock report backgrounds every rider can pick.
export async function getStockReportBackgrounds(): Promise<BasicResponse & { backgrounds: StockBackground[] }> {
  return asBasicResponse(
    window.fetch('/admin/report-backgrounds/list', getRequest()),
    { code: ErrorCode.STOCK_BACKGROUNDS_FAILED, message: 'Could not load the report backgrounds.' },
  );
}

export async function addStockReportBackground(file: File, sport: StockBackgroundSport): Promise<BasicResponse & { background: StockBackground }> {
  const body = new FormData();
  body.append('image', file);
  body.append('sport', sport);
  return asBasicResponse(
    window.fetch('/admin/report-backgrounds', { ...baseRequest(), method: 'POST', body }),
    { code: ErrorCode.STOCK_BACKGROUND_SAVE_FAILED, message: 'Could not add that image. Please try a JPEG or PNG under 15 MB.' },
  );
}

export async function orderStockReportBackgrounds(ids: string[]): Promise<BasicResponse> {
  return asBasicResponse(
    window.fetch('/admin/report-backgrounds/order', { ...getRequest(), method: 'POST', body: JSON.stringify({ ids }) }),
    { code: ErrorCode.STOCK_BACKGROUND_SAVE_FAILED, message: 'Could not save the order. Please try again.' },
  );
}

export async function removeStockReportBackground(id: string): Promise<BasicResponse> {
  return asBasicResponse(
    window.fetch(`/admin/report-backgrounds/${id}`, { ...getRequest(), method: 'DELETE' }),
    { code: ErrorCode.STOCK_BACKGROUND_SAVE_FAILED, message: 'Could not remove that background. Please try again.' },
  );
}

export async function deleteSocialReportBackground(id: string): Promise<BasicResponse> {
  return asBasicResponse(
    window.fetch(`/social-reports/backgrounds/${id}`, { ...getRequest(), method: 'DELETE' }),
    { code: ErrorCode.SOCIAL_REPORT_BACKGROUND_FAILED, message: 'Could not remove that background. Please try again.' },
  );
}

export async function likeWorkout(path: string): Promise<BasicResponse> {
  return window
    .fetch(`/workouts/like/${path}`, {
      method: 'POST',
      ...getRequest()
    })
    .then((r) => r.json());
}

export async function dislikeWorkout(path: string): Promise<BasicResponse> {
  return window
    .fetch(`/workouts/dislike/${path}`, {
      method: 'POST',
      ...getRequest()
    })
    .then((r) => r.json());
}

export async function starActivity(path: string, isStarred: boolean): Promise<BasicResponse> {
  let postData = {
    name: 'favourite',
    value: isStarred ? 0 : 1
  }

  return window
    .fetch(`/activities/${path}/attribute`, {
      method: 'POST',
      ...getRequest(),
      body: JSON.stringify(postData),
    })
    .then((r) => r.json());
}

export async function hideActivity(path: string, isHidden: boolean): Promise<BasicResponse> {
  let postData = {
    name: 'is_hidden',
    value: isHidden ? 0 : 1
  }

  return window
    .fetch(`/activities/${path}/attribute`, {
      method: 'POST',
      ...getRequest(),
      body: JSON.stringify(postData),
    })
    .then((r) => r.json());
}

export async function performMatch(activityPath: string, targetActivityPath: string): Promise<BasicResponse> {
  let postData = {};
  postData['activityPath'] = activityPath;
  postData['newMatchedActivityPath'] = targetActivityPath;
  return window
    .fetch(route('ActivityController@updateMatching'), {
      method: 'POST',
      body: JSON.stringify(postData), ...getRequest()
    })
    .then((r) => r.json());
}

interface PostActivityAttributeBody {
    path: string,
    "name"?: string,
    "desc"?: string,
    "favourite"?: 0 | 1,
    "flag"?: 0 | 1,
    "group_sharing"?: GroupSharingOptions,
}
export async function postActivityAttributes(body: PostActivityAttributeBody) {
    return window.fetch(`/activities/${body.path}/attributes`, {
        ...getRequest(),
        method: 'POST',
        body: JSON.stringify(body)
    }).then(simpleResponse)
}

/**
 * Applies one tag to one of the athlete's activities, or takes it off. One tag per request rather
 * than the whole list, so edits from two places can't overwrite each other. Returns the activity's
 * tags as they now stand.
 */
export async function addActivityTag(path: string, tag: string): Promise<{ custom_tags: string[] }> {
    return window.fetch(`/activities/${path}/tags`, {
        ...getRequest(),
        method: 'POST',
        body: JSON.stringify({ tag })
    }).then(simpleResponse<{ custom_tags: string[] }>)
}

export async function removeActivityTag(path: string, tag: string): Promise<{ custom_tags: string[] }> {
    return window.fetch(`/activities/${path}/tags`, {
        ...getRequest(),
        method: 'DELETE',
        body: JSON.stringify({ tag })
    }).then(simpleResponse<{ custom_tags: string[] }>)
}

/**
 * Applies one tag to, or takes it off, several activities at once. Returns each one's tags as they now
 * stand, and the paths that already had the most tags an activity can carry (left unchanged).
 */
export async function bulkActivityTag(paths: string[], tag: string, added: boolean): Promise<{ custom_tags: Record<string, string[]>, full: string[] }> {
    return window.fetch('/activities/bulk-tags', {
        ...getRequest(),
        method: 'POST',
        body: JSON.stringify({ paths, tag, added })
    }).then(simpleResponse<{ custom_tags: Record<string, string[]>, full: string[] }>)
}

export type CustomActivityTag = { name: string, color: string }

/** The athlete's own custom activity tags, on top of the fixed standard ones. */
export async function getActivityTagOptions(): Promise<{ custom: CustomActivityTag[] }> {
    return window.fetch('/activities/tags', getRequest()).then(simpleResponse<{ custom: CustomActivityTag[] }>)
}

/** Adds a new custom tag to the athlete's list; returns the updated list, plus `existing` for a duplicate. */
export async function createActivityTag(tag: CustomActivityTag): Promise<{ custom: CustomActivityTag[], existing?: string }> {
    return window.fetch('/activities/tags', {
        ...getRequest(),
        method: 'POST',
        body: JSON.stringify(tag)
    // 409: a tag of that name (in any case) already exists -- `existing` is its spelling, nothing was added.
    }).then((r) => r.status === 409 ? r.json() : simpleResponse<{ custom: CustomActivityTag[] }>(r))
}

/** Deletes a custom tag from the athlete's list (activities keep it); returns the updated list. */
export async function deleteActivityTag(name: string): Promise<{ custom: CustomActivityTag[] }> {
    return window.fetch('/activities/tags', {
        ...getRequest(),
        method: 'DELETE',
        body: JSON.stringify({ name })
    }).then(simpleResponse<{ custom: CustomActivityTag[] }>)
}

export type ReportPromise = Promise<{path: string, image_url: string, image_path: string, image_name: string}>
export async function getReport(activityPath: string, reportType: Omit<ReportKeys, 'se'>): ReportPromise {
    return window.fetch(`/activities/generate-breakthrough-report/${activityPath}/${reportType}`, getRequest()).then(r => r.json())
}

export async function getSessionReport(path: string): ReportPromise {
    return window.fetch(`/generate-session-report/${path}`, getRequest()).then(r => r.json())
}

type DeleteActivityPromise = Promise<{redirect: string}> 
export async function deleteActivity(path: string): DeleteActivityPromise{
    return window.fetch(
        `/activities/${path}`, 
        {
            ...baseRequest(),
            method: 'DELETE',
        }
    ).then(simpleResponse<DeleteActivityPromise>)
}

export type GroupDataPromise = Promise<Record<string, SharedActivity>>
interface GetGroupData {
    path: string,
    start?: number,
    end?: number,
    userTimeAdjust?: Record<string, number>
}
export async function getGroupData({path, start, end, userTimeAdjust}: GetGroupData): GroupDataPromise {
    let params = new URLSearchParams()
    if(start) params.append("start", start.toString())
    if(end) params.append("end", end.toString())
    if(userTimeAdjust) Object.entries(userTimeAdjust).forEach(([key, value]) => {params.append(`userTimeAdjust[${key}]`, value.toString());})
    return window.fetch(
        `/activities/${path}/group-data` + '?' + params,
        getRequest()
    ).then(simpleResponse<GroupDataPromise>)
}

export type ActivityStarredSegmentsPromise = Promise<{
    activity_start: string,
    code: number,
    message: string,
    premium: boolean,
    segments: XertSegment[],
    unit: string,
    xss: number,
}>
export async function getActivityStarredSegments(path: string): ActivityStarredSegmentsPromise {
    return window.fetch(
        `/activities/${path}/starred-segments`,
        getRequest()
    ).then(simpleResponse<ActivityStarredSegmentsPromise>)
}

export async function getStravaSegmentData(path:string, segment_id: number, effort_id: string): Promise<StravaSegmentInfo> {
    const params = new URLSearchParams({
        segment_id: segment_id.toString(),
        effort_id,
    })
    return window.fetch(
        `/activities/${path}/segment-data` + '?' + params,
        getRequest()
    ).then(simpleResponse<StravaSegmentInfo>)
}

export async function mergeActivities(activityPaths: string[], name: string | null = null): Promise<BasicResponse & { new_activity_path: string }> {
    return asBasicResponse(
      window.fetch(`/activities/merge`, {
        method: 'POST',
        ...getRequest(),
        body: JSON.stringify({ activity_paths: activityPaths, name }),
      }),
      {
        code: ErrorCode.MERGE_FAILED,
        message: 'Could not merge these activities. Please try again in a moment.',
      },
    );
}

export async function canDownloadOriginalFile(activityPath: string): Promise<boolean> {
  return window
    .fetch(`/activities/can-download/${activityPath}/`, {
      ...getRequest(),
    })
    .then((r) => r.json())
    .then((data) => {
      return data.can_download ?? false;
    });
}

export type ActivityRecordsResponse = ActivityRecordsData & {
  sig: Signature & { carb_bias: number };
  t0: number;
};

export async function getActivityRecordsData(path: string): Promise<ActivityRecordsResponse | null> {
  const response = await window.fetch(`/activities/${path}/data`, { ...baseRequest() });
  if (!response.ok) return null;
  return response.json();
}

// interval_duration is the length in seconds of the workout interval this point belongs to,
// repeated on every point of that interval.
export type WorkoutDataPoint = { seconds: number, power: number, mpa: number, interval_duration: number };

export async function getWorkoutData(path: string): Promise<{ data: WorkoutDataPoint[] } | null> {
  const response = await window.fetch(`/workout/${path}/data`, { ...baseRequest() });
  if (!response.ok) return null;
  return response.json();
}

export async function postFreshnessFeedback(formCat: 'Very fresh' | 'Fresh' | 'Tired' | 'Very tired' | null) {
  return window
    .fetch(`/account/settings/freshness-feedback`, {
      method: 'POST',
      ...getRequest(),
      body: JSON.stringify({ value: formCat }),
    })
    .then((r) => r.json());
}

export type HistoricalMmpConfirmResponse = {
  needs_confirm: true;
  missing_count: number;
  total_count: number;
};

export type HistoricalMmpDataResponse = {
  needs_confirm: false;
  durations: number[];
  powers: number[];
  attributions: Array<{ name: string; path: string; date: string; start_time?: number }>;
};

export type HistoricalMmpResponse = HistoricalMmpConfirmResponse | HistoricalMmpDataResponse;

export async function getHistoricalMmpCurve(
  startDate: string,
  endDate: string,
  confirm = false,
  maxDuration: number | null = null,
): Promise<HistoricalMmpResponse> {
  const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
  if (confirm) params.set('confirm', '1');
  if (maxDuration != null) params.set('max_duration', String(maxDuration));
  const r = await window.fetch(`/activities/historical-mmp?${params}`, getRequest());
  if (!r.ok) {
    throw new Error(`${r.status} ${r.statusText}`);
  }
  const data = await r.json();
  return data;
}

/* ===================================
    Rankings APIs
====================================== */

export async function getUserRankingStats(
  filters: { age?: string; gender?: string },
  rankingType: string,
  dataSource: string = 'breakthrough',
  activityPath?: string,
  weight?: string,
  mmpCurve?: { durations: number[]; powers: number[] },
  url: string = route('UserController@getUserStatistics'),
): Promise<UserStatsResponse> {
  const params = new URLSearchParams({ ranking_type: rankingType, data_source: dataSource });
  if (filters.gender) params.set('gender', filters.gender);
  if (filters.age) params.set('age', filters.age);
  if (dataSource === 'activity' && activityPath) params.set('activity_path', activityPath);
  if (weight) params.set('weight', weight);
  if (dataSource === 'historical_mmp' && mmpCurve && mmpCurve.durations.length) {
    params.set('mmp_durations', mmpCurve.durations.join(','));
    params.set('mmp_powers', mmpCurve.powers.join(','));
  }
  return window
    .fetch(url + '?' + params.toString(), getRequest())
    .then(simpleResponse<UserStatsResponse>);
}

export async function searchRankingActivities(query: string): Promise<RankingActivityOption[]> {
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  return window
    .fetch(route('ActivityController@getActivitiesForRankingPicker') + '?' + params.toString(), getRequest())
    .then(simpleResponse<RankingActivityOption[]>);
}

// For the "attach an activity/workout" picker in messages/comments -- unlike the ranking picker
// above, no power-data restriction, just the current user's own activities/workouts by name.
export type ExercisePickerOption = { path: string; name: string; start_date?: string };

export async function searchMyActivities(query: string): Promise<ExercisePickerOption[]> {
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  return window
    .fetch(route('ActivityController@getActivitiesForAttachmentPicker') + '?' + params.toString(), getRequest())
    .then(simpleResponse<ExercisePickerOption[]>);
}

export async function searchMyWorkouts(query: string): Promise<ExercisePickerOption[]> {
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  return window
    .fetch(route('WorkoutController@getWorkoutsForAttachmentPicker') + '?' + params.toString(), getRequest())
    .then(simpleResponse<ExercisePickerOption[]>);
}

// Lightweight fetch of a single activity/workout's Exercise-shaped data, for rendering an
// attached activity/workout card in a message/comment. Returns null (rather than throwing) on a
// 404 -- an unauthorized or deleted referenced activity/workout should render as "unavailable",
// not blow up the whole message.
export async function getExerciseSummary(type: 'activity' | 'workout', path: string): Promise<Exercise | null> {
  const routeName = type === 'activity' ? route('ActivityController@getActivitySummary', [path]) : route('WorkoutController@getWorkoutSummary', [path]);
  const response = await window.fetch(routeName, getRequest());
  if (!response.ok) return null;
  return response.json();
}

/* ===================================
    Strava Sync page
====================================== */

/** A Strava activity not yet in Xert, as /strava/activities/list lists it. */
export type StravaListedActivity = {
  id: number;
  name: string;
  type: string;
  /** The athlete's local wall-clock time, marked as UTC by Strava. */
  start_date_local: string;
  start_date: string;
  /** In the athlete's display units (km or mi). */
  distance?: number;
  /** Seconds. */
  elapsed_time?: number;
};

export type StravaActivityListResponse = BasicResponse & {
  data?: StravaListedActivity[];
};

/** Strava activities between two Unix times that aren't in Xert yet, of the synced types. */
export async function getStravaActivities(after: number, before: number, types: string[]): Promise<StravaActivityListResponse> {
  const params = new URLSearchParams({ before: String(before), after: String(after), save_filter: '0' });
  for (const t of types) params.append('filter[]', t);
  return asBasicResponse(
    window.fetch(`/strava/activities/list?${params}`, getRequest()),
    { code: ErrorCode.STRAVA_ACTIVITIES_FAILED, message: 'Could not load your Strava activities. Please try again.' },
  );
}

/**
 * Queue Strava activities to be imported into Xert. Free accounts are turned away by the `sync`
 * middleware with an empty answer (and a notification), so that comes back as a warning.
 */
export async function importStravaActivities(rows: StravaListedActivity[]): Promise<BasicResponse & { pending_activities?: number }> {
  const fallback = { code: ErrorCode.STRAVA_IMPORT_FAILED, message: 'Could not import those activities. Please try again.' };
  let response: Response;
  try {
    response = await window.fetch('/strava/activities', {
      ...getRequest(),
      method: 'POST',
      body: JSON.stringify({ rows: JSON.stringify(rows.map(({ id, name, start_date }) => ({ id, name, start_date }))) }),
    });
  } catch (e) {
    console.error('Request failed', e);
    return { success: false, ...NETWORK_ERROR_RESPONSE };
  }
  if (!response.ok) return errorFromResponse(response, fallback);
  const data = await response.json().catch(() => null);
  if (data && !Array.isArray(data) && 'pending_activities' in data) return { success: true, pending_activities: Number(data.pending_activities) };
  return { success: false, severity: 'warning', message: 'Free accounts can\'t import activities at this time.' };
}

/** POST to one of the older settings endpoints, which answer without `success`. */
async function postSetting(url: string, body: object, fallback: ErrorFallback): Promise<BasicResponse> {
  let response: Response;
  try {
    response = await window.fetch(url, { ...getRequest(), method: 'POST', body: JSON.stringify(body) });
  } catch (e) {
    console.error('Request failed', e);
    return { success: false, ...NETWORK_ERROR_RESPONSE };
  }
  return response.ok ? { success: true } : errorFromResponse(response, fallback);
}

/** The optional Strava activity types to sync, besides the sport's own. */
export function saveStravaActivityTypes(types: string[]): Promise<BasicResponse> {
  return postSetting('/strava/filters', { filter: types },
    { code: ErrorCode.STRAVA_SETTING_FAILED, message: 'Could not save your activity types. Please try again.' });
}

/** Whether Xert adds reports and summary data to the activities it sends to Strava. */
export function saveStravaAutoUpdate(on: boolean): Promise<BasicResponse> {
  return postSetting(route('UserController@postStravaAutoUpdate'), on ? { option: 'true' } : {},
    { code: ErrorCode.STRAVA_SETTING_FAILED, message: 'Could not save your Strava report setting. Please try again.' });
}

/** Which new activities sync automatically, Strava to Xert and Xert to Strava. */
export function saveStravaAutoSync(toXert: boolean, toStrava: boolean): Promise<BasicResponse> {
  const value = toXert && toStrava ? 'both' : toXert ? 'strava_to_xert' : toStrava ? 'xert_to_strava' : 'none';
  return postSetting(route('UserController@postDataAttribute'), { field: 'strava_auto_sync', value },
    { code: ErrorCode.STRAVA_SETTING_FAILED, message: 'Could not save your automatic sync setting. Please try again.' });
}

/** Garmin Connect Sync page: the optional activity types to sync, or whether scheduled workouts are sent to Garmin. */
export function saveGarminSyncSettings(url: string, settings: { filter?: string[], send_workouts?: boolean }): Promise<BasicResponse> {
  return postSetting(url, settings,
    { code: ErrorCode.SYNC_SETTING_FAILED, message: 'Could not save your Garmin Connect setting. Please try again.' });
}

/** Ask Garmin to send the activities between two times (ms). Garmin sends them later, and the user is notified. */
export function requestGarminActivities(url: string, start: number, end: number): Promise<BasicResponse> {
  return postSetting(`${url}/${start}/${end}`, {},
    { code: ErrorCode.GARMIN_IMPORT_REQUEST_FAILED, message: 'Could not ask Garmin for your activities. Please try again.' });
}

/** Zwift and Wahoo Sync pages: sending scheduled workouts, syncing activities and their types. */
export function saveWorkoutPlayerSyncSettings(url: string, settings: { send_workouts?: boolean, sync_activities?: boolean, sync_activity_types?: string[] }): Promise<BasicResponse> {
  return postSetting(url, settings,
    { code: ErrorCode.SYNC_SETTING_FAILED, message: 'Could not save your setting. Please try again.' });
}

/** Zwift and Wahoo Sync pages: remove a workout sent to the app, or unschedule it. `url` comes with the workout. */
export function removeWorkoutPlayerWorkout(url: string): Promise<BasicResponse> {
  return postSetting(url, {},
    { code: ErrorCode.SYNC_WORKOUT_REMOVE_FAILED, message: 'Could not remove that workout. Please try again.' });
}

/** Whether the user's name and photo show on other riders' group reports. */
export async function saveGroupReportPrivacy(show: boolean): Promise<BasicResponse> {
  return asBasicResponse(
    window.fetch('/social-reports/privacy', {
      ...getRequest(),
      method: 'POST',
      body: JSON.stringify({ show_on_group_reports: show }),
    }),
    { code: ErrorCode.GROUP_REPORT_PRIVACY_FAILED, message: 'Could not save your group report setting. Please try again.' },
  );
}
